
import Vue from "vue"
import Component from "vue-class-component"
import {Prop} from "vue-property-decorator"
import {date} from "quasar"
import PopupDate from "@/components/PopupDate.vue"

export interface DateDropdownGroup {
  id: string
  keys: Array<string>
}
export interface RangeFilter {
  from: string
  to: string
}
interface RangeFilterConfig {
  key: string
  label: string
}

// every date filter key this group's dropdown can offer, and its display
// label - see MembersView.vue's DATE_DROPDOWN_GROUPS for how keys are
// grouped into rows
const RANGE_FILTER_CONFIGS: Array<RangeFilterConfig> = [
  {key: "created", label: "Created"},
  {key: "updated", label: "Updated"},
  {key: "firstLogin", label: "First Login"},
  {key: "lastLogin", label: "Last Login"},
  {key: "contactRequested", label: "Contact Requested"},
  {key: "projectionModelled", label: "Projection Modelled"},
  {key: "riskProfileAssessed", label: "Risk Profile Assessed"},
  {key: "insuranceAssessed", label: "Insurance Assessed"},
  {key: "contributionCalculated", label: "Contribution Calculated"},
  {key: "ttrCalculated", label: "TTR Calculated"},
  {key: "beneficiaryAssessed", label: "Beneficiary Assessed"},
  {key: "reportDownloaded", label: "Report Downloaded"},
  {key: "adviceDownloaded", label: "Advice Downloaded"},
]

const labelFor = (key: string): string =>
  RANGE_FILTER_CONFIGS.find(cfg => cfg.key === key)?.label ?? key

const YEARS_AGO_STEPS = [1, 2, 3, 5]
const DATE_FORMAT = "DD/MM/YYYY"

const yearsAgoRange = (years: number): RangeFilter => {
  const now = new Date()
  const past = new Date(now)
  past.setFullYear(now.getFullYear() - years)
  return {
    from: date.formatDate(past, DATE_FORMAT),
    to: "",
  }
}

// a single dropdown + From/To + "last N years" quick-set row - the dropdown
// lets the user pick which one of the group's date filters this row
// currently applies to; used (in pairs) for each row of MembersView's
// collapsible "Activity" section
@Component({components: {PopupDate}})
export default class DateRangeFilterGroup extends Vue {
  @Prop()
  group: DateDropdownGroup

  // key of the date filter (within group.keys) currently selected in the
  // dropdown - use with the .sync modifier
  @Prop()
  selection: string

  // the from/to range for the currently selected key - use with the .sync
  // modifier
  @Prop()
  range: RangeFilter

  get rangeOptions(): Array<{label: string; value: string}> {
    return this.group.keys.map(key => ({label: labelFor(key), value: key}))
  }

  private updateRange(field: "from" | "to", value: string) {
    this.$emit("update:range", {...this.range, [field]: value})
  }

  // returns 1/2/3/5 if the range's "from" currently matches one of the "N
  // years ago" presets (and "to" is empty), otherwise 0 - used to work out
  // the next step in the cycle
  private get yearsAgoApplied(): number {
    if (this.range.to !== "") return 0
    return YEARS_AGO_STEPS.find(years => this.range.from === yearsAgoRange(years).from) ?? 0
  }

  private get nextYearsAgo(): number {
    const index = YEARS_AGO_STEPS.indexOf(this.yearsAgoApplied)
    return YEARS_AGO_STEPS[(index + 1) % YEARS_AGO_STEPS.length]
  }

  get lastYearTooltip(): string {
    const years = this.nextYearsAgo
    return `Last ${years} year${years === 1 ? "" : "s"}`
  }

  private setLastYear() {
    this.$emit("update:range", yearsAgoRange(this.nextYearsAgo))
  }
}
